import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Kunde, Bericht, Elektroliste, Poolzeichnung } from '../types';
import kundeService from '../services/kundeService';
import berichtService from '../services/berichtService';
import elektrolisteService from '../services/elektrolisteService';
import poolzeichnungService from '../services/poolzeichnungService';
import { Icon, LoadingScreen, EmptyState, Spinner, fehlerText } from '../components/ui';
import { ZurueckLink } from '../components/ZurueckLink';
import KundenErinnerungen from '../components/KundenErinnerungen';
import { markiereNeu, ankunft } from '../utils/ankunft';

const KundeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [berichte, setBerichte] = useState<Bericht[]>([]);
  const [elektrolisten, setElektrolisten] = useState<Elektroliste[]>([]);
  const [downloadingListeId, setDownloadingListeId] = useState<number | null>(null);
  const [poolzeichnungen, setPoolzeichnungen] = useState<Poolzeichnung[]>([]);
  const [poolzeichnungenFehler, setPoolzeichnungenFehler] = useState<string | null>(null);
  const [downloadingZeichnungId, setDownloadingZeichnungId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedBerichtId, setExpandedBerichtId] = useState<number | null>(null);
  const [downloadingPdfId, setDownloadingPdfId] = useState<number | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [erinnerungAnlegen, setErinnerungAnlegen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    vorname: '', nachnameFirma: '', strasse: '', plz: '', ort: '',
    mobiltelefon: '', email: '', entfernungKm: '', anfahrtMinuten: ''
  });

  useEffect(() => {
    if (id) loadData(Number(id));
    // Lädt je Kunde neu; `loadData` entsteht bei jedem Rendern neu und würde sonst eine Schleife auslösen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async (kundeId: number) => {
    setLoading(true);
    setPoolzeichnungenFehler(null);
    try {
      const [kundeData, berichteData, listenData, zeichnungenData] = await Promise.all([
        kundeService.getById(kundeId),
        berichtService.getByKundeId(kundeId),
        elektrolisteService.getByKundeId(kundeId),
        poolzeichnungService.getByKundeId(kundeId).catch((err) => {
          setPoolzeichnungenFehler(fehlerText(err, 'Poolzeichnungen konnten nicht geladen werden'));
          return [];
        }),
      ]);
      setKunde(kundeData);
      setBerichte(berichteData);
      setElektrolisten(listenData);
      setPoolzeichnungen(zeichnungenData);
    } catch {
      toast.error('Kunde nicht gefunden');
      navigate('/kunden');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBericht = async (bericht: Bericht) => {
    if (window.confirm(`Bericht vom ${formatDate(bericht.datum)} löschen?`)) {
      try {
        await berichtService.delete(bericht.id);
        toast.success('Bericht gelöscht');
        setBerichte(prev => prev.filter(b => b.id !== bericht.id));
      } catch (err) {
        toast.error(fehlerText(err, 'Bericht konnte nicht gelöscht werden'));
      }
    }
  };

  const handleDownloadPdf = async (bericht: Bericht) => {
    setDownloadingPdfId(bericht.id);
    try {
      await berichtService.downloadPdf(bericht.id);
      toast.success('PDF heruntergeladen');
    } catch (err) {
      toast.error(fehlerText(err, 'PDF konnte nicht erzeugt werden'));
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const handleDeleteElektroliste = async (liste: Elektroliste) => {
    if (window.confirm(`Elektroliste „${liste.titel}“ vom ${formatDate(liste.datum)} löschen?`)) {
      try {
        await elektrolisteService.delete(liste.id);
        toast.success('Elektroliste gelöscht');
        setElektrolisten(prev => prev.filter(l => l.id !== liste.id));
      } catch (err) {
        toast.error(fehlerText(err, 'Elektroliste konnte nicht gelöscht werden'));
      }
    }
  };

  const handleDownloadElektroliste = async (liste: Elektroliste) => {
    setDownloadingListeId(liste.id);
    try {
      await elektrolisteService.downloadPdf(liste.id);
      toast.success('PDF heruntergeladen');
    } catch (err) {
      toast.error(fehlerText(err, 'PDF konnte nicht erzeugt werden'));
    } finally {
      setDownloadingListeId(null);
    }
  };

  const handleDeletePoolzeichnung = async (zeichnung: Poolzeichnung) => {
    if (window.confirm(`Poolzeichnung „${zeichnung.titel}“ vom ${formatDate(zeichnung.datum)} löschen?`)) {
      try {
        await poolzeichnungService.delete(zeichnung.id);
        toast.success('Poolzeichnung gelöscht');
        setPoolzeichnungen(prev => prev.filter(z => z.id !== zeichnung.id));
      } catch (err) {
        toast.error(fehlerText(err, 'Poolzeichnung konnte nicht gelöscht werden'));
      }
    }
  };

  const handleDownloadPoolzeichnung = async (zeichnung: Poolzeichnung) => {
    setDownloadingZeichnungId(zeichnung.id);
    try {
      await poolzeichnungService.downloadPdf(zeichnung.id);
      toast.success('PDF heruntergeladen');
    } catch (err) {
      toast.error(fehlerText(err, 'PDF konnte nicht erzeugt werden'));
    } finally {
      setDownloadingZeichnungId(null);
    }
  };

  const handleEditOpen = () => {
    if (!kunde) return;
    setEditFormData({
      vorname: kunde.vorname || '',
      nachnameFirma: kunde.nachnameFirma,
      strasse: kunde.strasse || '',
      plz: kunde.plz || '',
      ort: kunde.ort || '',
      mobiltelefon: kunde.mobiltelefon || '',
      email: kunde.email || '',
      entfernungKm: kunde.entfernungKm ? String(kunde.entfernungKm) : '',
      anfahrtMinuten: kunde.anfahrtMinuten ? String(kunde.anfahrtMinuten) : '',
    });
    setShowEditForm(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kunde) return;
    try {
      const updated = await kundeService.update(kunde.id, {
        vorname: editFormData.vorname || undefined,
        nachnameFirma: editFormData.nachnameFirma,
        strasse: editFormData.strasse || undefined,
        plz: editFormData.plz || undefined,
        ort: editFormData.ort || undefined,
        mobiltelefon: editFormData.mobiltelefon || undefined,
        email: editFormData.email || undefined,
        entfernungKm: editFormData.entfernungKm ? Number(editFormData.entfernungKm) : 0,
        anfahrtMinuten: editFormData.anfahrtMinuten ? Number(editFormData.anfahrtMinuten) : 0,
      });
      setKunde(updated);
      setShowEditForm(false);
      markiereNeu(`kunde-${updated.id}`);
      toast.success('Kunde gespeichert');
    } catch (err) {
      toast.error(fehlerText(err, 'Kunde konnte nicht gespeichert werden'));
    }
  };

  const formatDate = (d: string) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return d;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!kunde) return null;

  const kundeName = [kunde.vorname, kunde.nachnameFirma].filter(Boolean).join(' ');
  const kundeAnkunft = ankunft(`kunde-${kunde.id}`);

  return (
    <div className="p-4 md:p-6">
      {/* Back button + Title */}
      <div className="mb-4">
        <div className="mb-2">
          <ZurueckLink fallback="/kunden" fallbackLabel="Zurück zu den Kunden" />
        </div>
        <div className="flex items-center gap-2">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{kundeName}</h1>
          <button
            onClick={handleEditOpen}
            className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
            title="Kunde bearbeiten"
          >
            <Icon.Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => setErinnerungAnlegen(true)}
            title="Erinnerung anlegen"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-amber-300 hover:bg-amber-50 transition-colors"
          >
            <Icon.Glocke className="w-4 h-4 text-amber-500" />
            <span className="hidden sm:inline">Erinnerung anlegen</span>
          </button>
        </div>
      </div>

      <KundenErinnerungen
        kundeId={kunde.id}
        anlegenOffen={erinnerungAnlegen}
        onAnlegenSchliessen={() => setErinnerungAnlegen(false)}
      />

      {/* Kunde Info Card */}
      <div ref={kundeAnkunft.ref} className={`bg-white rounded-lg shadow-sm border border-gray-100 p-4 md:p-5 mb-6 ${kundeAnkunft.className}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(kunde.strasse || kunde.plz || kunde.ort) && (
            <div>
              <p className="text-xs text-gray-500 font-semibold">Adresse</p>
              {kunde.strasse && <p className="text-sm text-gray-800">{kunde.strasse}</p>}
              <p className="text-sm text-gray-800">{[kunde.plz, kunde.ort].filter(Boolean).join(' ')}</p>
              {(() => {
                const dest = kunde.lat && kunde.lng
                  ? `${kunde.lat},${kunde.lng}`
                  : encodeURIComponent([kunde.strasse, kunde.plz, kunde.ort].filter(Boolean).join(', '));
                return (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${dest}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1 text-xs text-brand-600 hover:text-brand-800"
                  >
                    <Icon.MapPin className="w-3.5 h-3.5" />
                    Navigation starten
                  </a>
                );
              })()}
            </div>
          )}
          {kunde.mobiltelefon && (
            <div>
              <p className="text-xs text-gray-500 font-semibold">Telefon</p>
              <a href={`tel:${kunde.mobiltelefon}`} className="text-sm text-brand-600 hover:text-brand-800">
                {kunde.mobiltelefon}
              </a>
            </div>
          )}
          {kunde.email && (
            <div>
              <p className="text-xs text-gray-500 font-semibold">E-Mail</p>
              <a href={`mailto:${kunde.email}`} className="text-sm text-brand-600 hover:text-brand-800">
                {kunde.email}
              </a>
            </div>
          )}
          {(kunde.entfernungKm != null && kunde.entfernungKm > 0) || (kunde.anfahrtMinuten != null && kunde.anfahrtMinuten > 0) ? (
            <div>
              <p className="text-xs text-gray-500 font-semibold">Anfahrt</p>
              <p className="text-sm text-gray-800">
                {[
                  kunde.entfernungKm && kunde.entfernungKm > 0 ? `${kunde.entfernungKm} km` : null,
                  kunde.anfahrtMinuten && kunde.anfahrtMinuten > 0 ? `${kunde.anfahrtMinuten} Min.` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            </div>
          ) : null}
          {kunde.wisoKundennummer != null && (
            <div>
              <p className="text-xs text-gray-500 font-semibold">WISO Kundennr.</p>
              <p className="text-sm text-gray-500 font-mono">{kunde.wisoKundennummer}</p>
            </div>
          )}
        </div>
      </div>

      {/* Berichte Section */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base md:text-lg font-semibold text-gray-900">
          Berichte
          <span className="ml-2 text-sm font-normal text-gray-400">({berichte.length})</span>
        </h2>
        <button
          onClick={() => navigate(`/berichte/neu?kundeId=${kunde.id}`)}
          className="bg-brand-600 text-white px-3 py-1.5 rounded-lg hover:bg-brand-700 transition-colors text-sm flex items-center gap-1"
        >
          <Icon.Plus className="w-4 h-4" />
          Bericht anlegen
        </button>
      </div>

      {berichte.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <EmptyState
            icon={<Icon.Bericht className="h-6 w-6" />}
            title="Noch keine Berichte für diesen Kunden"
            action={
              <button
                onClick={() => navigate(`/berichte/neu?kundeId=${kunde.id}`)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                <Icon.Plus className="h-4 w-4" />Bericht anlegen
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-2">
          {berichte.map((bericht) => {
            const isExpanded = expandedBerichtId === bericht.id;
            const isDownloading = downloadingPdfId === bericht.id;
            const arbeitszeitNetto = bericht.arbeitszeitStart && bericht.arbeitszeitEnde
              ? calcMinutes(bericht.arbeitszeitStart, bericht.arbeitszeitEnde) - bericht.pauseMinuten
              : 0;

            return (
              <div key={bericht.id} className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className="flex items-center p-3 md:p-4">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => setExpandedBerichtId(isExpanded ? null : bericht.id)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-brand-700 whitespace-nowrap">
                        {formatDate(bericht.datum)}
                      </span>
                      {bericht.erstelltVon && (
                        <span className="text-xs text-gray-400">von {bericht.erstelltVon}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mt-0.5 truncate">{bericht.ausgefuehrteArbeiten}</p>
                    <div className="flex gap-3 mt-1 text-xs text-gray-400">
                      <span>{bericht.fahrzeitMinuten} Min. Fahrt</span>
                      <span>{arbeitszeitNetto} Min. Arbeit</span>
                      <span>{bericht.gefahreneKm} km</span>
                    </div>
                  </div>

                  {/* PDF */}
                  <button
                    onClick={() => handleDownloadPdf(bericht)}
                    disabled={isDownloading}
                    className="flex-shrink-0 p-2 mr-1 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                    title="PDF herunterladen"
                  >
                    {isDownloading ? (
                      <Spinner className="h-5 w-5" />
                    ) : (
                      <Icon.Download className="h-5 w-5" />
                    )}
                  </button>

                  {/* Expand */}
                  <button
                    onClick={() => setExpandedBerichtId(isExpanded ? null : bericht.id)}
                    className="flex-shrink-0 p-1 text-gray-400"
                    aria-label={isExpanded ? 'Details einklappen' : 'Details aufklappen'}
                  >
                    <Icon.ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Expanded */}
                {isExpanded && (
                  <div className="ui-aufklappen border-t border-gray-100 p-3 md:p-4 bg-gray-50">
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 mb-1">Ausgeführte Arbeiten</h3>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{bericht.ausgefuehrteArbeiten}</p>
                    </div>

                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 mb-1">Zeiten</h3>
                      <div className="text-sm text-gray-700 space-y-0.5">
                        <div>Fahrzeit: {bericht.abfahrtZeit}–{bericht.ankunftZeit} ({bericht.fahrzeitMinuten} Min.)</div>
                        <div>Arbeitszeit: {bericht.arbeitszeitStart}–{bericht.arbeitszeitEnde} ({arbeitszeitNetto} Min. netto)</div>
                        {bericht.pauseMinuten > 0 && <div>Pause: {bericht.pauseMinuten} Min.</div>}
                        <div>Gefahrene km: {bericht.gefahreneKm}</div>
                      </div>
                    </div>

                    {bericht.hinweise && (
                      <div className="mb-3">
                        <h3 className="text-xs font-semibold text-gray-500 mb-1">Hinweise</h3>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{bericht.hinweise}</p>
                      </div>
                    )}

                    {bericht.materialien && bericht.materialien.length > 0 && (
                      <div className="mb-3">
                        <h3 className="text-xs font-semibold text-gray-500 mb-1">Material</h3>
                        <div className="text-sm text-gray-700">
                          {bericht.materialien.map((m, i) => (
                            <div key={i}>{m.menge} {m.einheit} {m.artikelbezeichnung}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {bericht.mitarbeiter && bericht.mitarbeiter.length > 0 && (
                      <div className="mb-3">
                        <h3 className="text-xs font-semibold text-gray-500 mb-1">Mitarbeiter</h3>
                        <div className="text-sm text-gray-700">
                          {bericht.mitarbeiter.map((m, i) => (
                            <div key={i}>{m.name}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {bericht.unterschriftBild && (
                      <div className="mb-3">
                        <h3 className="text-xs font-semibold text-gray-500 mb-1">Unterschrift</h3>
                        <img src={bericht.unterschriftBild} alt="Unterschrift"
                          className="border border-gray-200 rounded bg-white max-h-20" />
                        {bericht.unterschriftName && (
                          <p className="text-xs text-gray-500 mt-1">{bericht.unterschriftName}</p>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 pt-3 border-t border-gray-200 flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/berichte/${bericht.id}/bearbeiten`)}
                        className="bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700 transition-colors flex items-center gap-2 text-sm"
                      >
                        <Icon.Edit className="h-4 w-4" />
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(bericht)}
                        disabled={isDownloading}
                        className="bg-white border border-brand-200 text-brand-700 px-4 py-2 rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
                      >
                        <Icon.Download className="h-4 w-4" />
                        PDF
                      </button>
                      <button
                        onClick={() => handleDeleteBericht(bericht)}
                        className="text-red-600 border border-red-200 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors text-sm flex items-center gap-2"
                      >
                        <Icon.Trash className="w-4 h-4" />
                        Löschen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Elektrolisten Section */}
      <div className="flex justify-between items-center mt-8 mb-3">
        <h2 className="text-base md:text-lg font-semibold text-gray-900">
          Elektrolisten
          <span className="ml-2 text-sm font-normal text-gray-400">({elektrolisten.length})</span>
        </h2>
        <button
          onClick={() => navigate(`/elektrolisten/neu?kundeId=${kunde.id}`)}
          className="bg-brand-600 text-white px-3 py-1.5 rounded-lg hover:bg-brand-700 transition-colors text-sm flex items-center gap-1"
        >
          <Icon.Plus className="w-4 h-4" />
          Elektroliste anlegen
        </button>
      </div>

      {elektrolisten.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <EmptyState
            icon={<Icon.Blitz className="h-6 w-6" />}
            title="Noch keine Elektrolisten für diesen Kunden"
            hint="Die Elektroliste sagt dem Elektriker, welche Bauteile er vor Ort anschließen muss."
            action={
              <button
                onClick={() => navigate(`/elektrolisten/neu?kundeId=${kunde.id}`)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                <Icon.Plus className="h-4 w-4" />Elektroliste anlegen
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-2">
          {elektrolisten.map((liste) => {
            const a = ankunft(`elektroliste-${liste.id}`);
            const isDownloading = downloadingListeId === liste.id;
            const anzahl = liste.positionen.length;
            return (
              <div key={liste.id} ref={a.ref} className={`bg-white rounded-lg shadow-sm border border-gray-100 ${a.className}`}>
                <div className="flex items-center p-3 md:p-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/elektrolisten/${liste.id}/bearbeiten`)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="text-sm font-semibold text-gray-900 truncate">{liste.titel}</div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {[
                        formatDate(liste.datum),
                        `${anzahl} ${anzahl === 1 ? 'Bauteil' : 'Bauteile'}`,
                        liste.erstelltVon ? `von ${liste.erstelltVon}` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </button>
                  <button
                    onClick={() => handleDownloadElektroliste(liste)}
                    disabled={isDownloading}
                    className="flex-shrink-0 p-2 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                    title="PDF herunterladen"
                  >
                    {isDownloading ? <Spinner className="h-5 w-5" /> : <Icon.Download className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={() => navigate(`/elektrolisten/${liste.id}/bearbeiten`)}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                    title="Bearbeiten"
                  >
                    <Icon.Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteElektroliste(liste)}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Löschen"
                  >
                    <Icon.Trash className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Poolzeichnungen Section */}
      <div className="flex justify-between items-center mt-8 mb-3">
        <h2 className="text-base md:text-lg font-semibold text-gray-900">
          Poolzeichnungen
          <span className="ml-2 text-sm font-normal text-gray-400">({poolzeichnungen.length})</span>
        </h2>
        <button
          onClick={() => navigate(`/poolzeichnungen/neu?kundeId=${kunde.id}`)}
          className="bg-brand-600 text-white px-3 py-1.5 rounded-lg hover:bg-brand-700 transition-colors text-sm flex items-center gap-1"
        >
          <Icon.Plus className="w-4 h-4" />
          Poolzeichnung anlegen
        </button>
      </div>

      {poolzeichnungenFehler ? (
        <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p>{poolzeichnungenFehler}</p>
          <button type="button" onClick={() => loadData(kunde.id)} className="mt-2 font-medium underline">
            Erneut laden
          </button>
        </div>
      ) : poolzeichnungen.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <EmptyState
            icon={<Icon.Grundriss className="h-6 w-6" />}
            title="Noch keine Poolzeichnungen für diesen Kunden"
            hint="Die Poolzeichnung zeigt Innen-, Außen- und Grubenmaß sowie die Tiefe der Baugrube."
            action={
              <button
                onClick={() => navigate(`/poolzeichnungen/neu?kundeId=${kunde.id}`)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                <Icon.Plus className="h-4 w-4" />Poolzeichnung anlegen
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-2">
          {poolzeichnungen.map((zeichnung) => {
            const a = ankunft(`poolzeichnung-${zeichnung.id}`);
            const isDownloading = downloadingZeichnungId === zeichnung.id;
            const m = zeichnung.masse;
            const einbauten = [m.skimmer && 'Skimmer', m.gegenstromanlage && 'Gegenstromanlage'].filter(Boolean).join(' und ');
            return (
              <div key={zeichnung.id} ref={a.ref} className={`bg-white rounded-lg shadow-sm border border-gray-100 ${a.className}`}>
                <div className="flex items-center p-3 md:p-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/poolzeichnungen/${zeichnung.id}/bearbeiten`)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="text-sm font-semibold text-gray-900 truncate">{zeichnung.titel}</div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {[
                        `${m.innenLaenge} × ${m.innenBreite} × ${m.innenHoehe} mm`,
                        einbauten ? `mit ${einbauten}` : null,
                        formatDate(zeichnung.datum),
                        zeichnung.erstelltVon ? `von ${zeichnung.erstelltVon}` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </button>
                  <button
                    onClick={() => handleDownloadPoolzeichnung(zeichnung)}
                    disabled={isDownloading}
                    className="flex-shrink-0 p-2 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                    title="PDF herunterladen"
                  >
                    {isDownloading ? <Spinner className="h-5 w-5" /> : <Icon.Download className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={() => navigate(`/poolzeichnungen/${zeichnung.id}/bearbeiten`)}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                    title="Bearbeiten"
                  >
                    <Icon.Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeletePoolzeichnung(zeichnung)}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Löschen"
                  >
                    <Icon.Trash className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {showEditForm && (
        <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="ui-pop bg-white rounded-lg p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Kunde bearbeiten</h2>
            <form onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Vorname</label>
                  <input type="text" value={editFormData.vorname}
                    onChange={(e) => setEditFormData({ ...editFormData, vorname: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nachname / Firma *</label>
                  <input type="text" required value={editFormData.nachnameFirma}
                    onChange={(e) => setEditFormData({ ...editFormData, nachnameFirma: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Straße</label>
                <input type="text" value={editFormData.strasse}
                  onChange={(e) => setEditFormData({ ...editFormData, strasse: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">PLZ</label>
                  <input type="text" value={editFormData.plz}
                    onChange={(e) => setEditFormData({ ...editFormData, plz: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Ort</label>
                  <input type="text" value={editFormData.ort}
                    onChange={(e) => setEditFormData({ ...editFormData, ort: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Telefon</label>
                  <input type="text" value={editFormData.mobiltelefon}
                    onChange={(e) => setEditFormData({ ...editFormData, mobiltelefon: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">E-Mail</label>
                  <input type="email" value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Entfernung (km)</label>
                  <input type="number" min={0} step="0.1" value={editFormData.entfernungKm}
                    onChange={(e) => setEditFormData({ ...editFormData, entfernungKm: e.target.value })}
                    placeholder="z.B. 25.5"
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Anfahrtzeit (Min.)</label>
                  <input type="number" min={0} step="1" value={editFormData.anfahrtMinuten}
                    onChange={(e) => setEditFormData({ ...editFormData, anfahrtMinuten: e.target.value })}
                    placeholder="z.B. 30"
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowEditForm(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm">
                  Abbrechen
                </button>
                <button type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm">
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

function calcMinutes(start: string, end: string): number {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff : 0;
}

export default KundeDetail;
